import React from 'react';
import { Badge } from '../Badge';
import { SearchIcon, MailIcon, ChevronUpIcon, ChevronDownIcon, ChevronsUpDownIcon, ChevronRightIcon, CheckIcon, Settings2Icon, ExternalLinkIcon } from 'lucide-react';
import { TABLE_COLUMNS, getTypeBadgeVariant, getStatusBadgeVariant, DEFAULT_REVIEW_STATE, type ColumnId, type SortColumn, type SortDirection, type DisplayRow, type ReviewDocument, type DocReviewState, type CaseIssue } from './types';
import { CheckCircleIcon, TagIcon } from 'lucide-react';
interface DocumentTableProps {
  paginatedRows: DisplayRow[];
  gridTemplate: string;
  isColumnVisible: (id: ColumnId) => boolean;
  visibleColumns: Set<ColumnId>;
  columnDropdownOpen: boolean;
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  selectedDoc: ReviewDocument | null;
  expandedThreads: Set<string>;
  totalFiltered: number;
  showingFrom: number;
  showingTo: number;
  totalPages: number;
  safeCurrentPage: number;
  pageSize: number;
  pageSizeDropdownOpen: boolean;
  handleSort: (col: SortColumn) => void;
  toggleThread: (threadId: string) => void;
  toggleColumn: (id: ColumnId) => void;
  setColumnDropdownOpen: (v: boolean) => void;
  setSelectedDoc: (doc: ReviewDocument | null) => void;
  setHoveredDoc: (doc: ReviewDocument | null) => void;
  handleRowMouseEnter: (doc: ReviewDocument, e: React.MouseEvent<HTMLDivElement>) => void;
  handleRowMouseLeave: () => void;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: (size: 25 | 50 | 100) => void;
  setPageSizeDropdownOpen: (v: boolean) => void;
  clearAllFilters: () => void;
  getThreadSize: (threadId: string) => number;
  columnDropdownRef: React.RefObject<HTMLDivElement | null>;
  pageSizeDropdownRef: React.RefObject<HTMLDivElement | null>;
  docStates: Record<string, DocReviewState>;
  caseIssues: CaseIssue[];
  docIssues: Record<string, string[]>;
  issueFilter: string | null;
  onIssueClick: (issueId: string) => void;
}
export function DocumentTable({
  paginatedRows,
  gridTemplate,
  isColumnVisible,
  visibleColumns,
  columnDropdownOpen,
  sortColumn,
  sortDirection,
  selectedDoc,
  expandedThreads,
  totalFiltered,
  showingFrom,
  showingTo,
  totalPages,
  safeCurrentPage,
  pageSize,
  pageSizeDropdownOpen,
  handleSort,
  toggleThread,
  toggleColumn,
  setColumnDropdownOpen,
  setSelectedDoc,
  setHoveredDoc,
  handleRowMouseEnter,
  handleRowMouseLeave,
  setCurrentPage,
  setPageSize,
  setPageSizeDropdownOpen,
  clearAllFilters,
  getThreadSize,
  columnDropdownRef,
  pageSizeDropdownRef,
  docStates,
  caseIssues,
  docIssues,
  issueFilter,
  onIssueClick
}: DocumentTableProps) {
  function SortIcon({
    column
  }: {
    column: SortColumn;
  }) {
    if (sortColumn !== column) {
      return <ChevronsUpDownIcon className="w-3 h-3 ml-1 opacity-40" data-id="element-217" data-mp="1wxnrz65bhivo" />;
    }
    return sortDirection === 'asc' ? <ChevronUpIcon className="w-3 h-3 ml-1 text-amber" data-id="element-218" data-mp="1getwjfnjvm" /> : <ChevronDownIcon className="w-3 h-3 ml-1 text-amber" data-id="element-219" data-mp="3rb5cslec81n0" />;
  }
  return <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${selectedDoc ? 'pr-[480px]' : ''}`} data-id="element-220" data-mp="3kfats1thpvs">
      {/* Table Header */}
      <div className="grid gap-2 px-6 py-2.5 border-b border-navy-lighter bg-navy text-xs font-semibold text-gray-400 uppercase tracking-wider shrink-0 select-none" style={{
      gridTemplateColumns: gridTemplate
    }} data-id="element-221" data-mp="cdsadcudzpqu">
        {isColumnVisible('date') && <button onClick={() => handleSort('date')} className={`flex items-center text-left transition-colors hover:text-gray-200 ${sortColumn === 'date' ? 'text-amber' : ''}`} data-id="element-222" data-mp="3ktlstx8a3baj">
            Date
            <SortIcon column="date" data-id="element-223" data-mp="3us1kq2f4qol3" />
          </button>}
        {isColumnVisible('type') && <button onClick={() => handleSort('type')} className={`flex items-center text-left transition-colors hover:text-gray-200 ${sortColumn === 'type' ? 'text-amber' : ''}`} data-id="element-224" data-mp="15393kcptzao4">
            Type
            <SortIcon column="type" data-id="element-225" data-mp="2iyr6vtt11m5m" />
          </button>}
        {isColumnVisible('subject') && <button onClick={() => handleSort('subject')} className={`flex items-center text-left transition-colors hover:text-gray-200 ${sortColumn === 'subject' ? 'text-amber' : ''}`} data-id="element-226" data-mp="2e1h8honnu05n">
            Subject
            <SortIcon column="subject" data-id="element-227" data-mp="31wtnplbf8csc" />
          </button>}
        {isColumnVisible('summary') && <div data-id="element-228" data-mp="2wys674tn8liz">AI Summary</div>}
        {isColumnVisible('sender') && <button onClick={() => handleSort('sender')} className={`flex items-center text-left transition-colors hover:text-gray-200 ${sortColumn === 'sender' ? 'text-amber' : ''}`} data-id="element-229" data-mp="2wwfp0tvpmf6z">
            Sender
            <SortIcon column="sender" data-id="element-230" data-mp="3kn1p9yzh2v6f" />
          </button>}
        {isColumnVisible('status') && <div data-id="element-231" data-mp="3fpjc1wef9tsm">Status</div>}
        {isColumnVisible('thread') && <div className="text-center" title="Email thread" data-id="element-232" data-mp="355tpxeq7d1g">
            <MailIcon className="w-3 h-3 mx-auto opacity-40" data-id="element-233" data-mp="35ozygv0kczn" />
          </div>}
        {isColumnVisible('action') && <div className="flex justify-end" data-id="element-234" data-mp="81w88gavu0kr">
            <ExternalLinkIcon className="w-3 h-3 opacity-40" data-id="element-235" data-mp="3pbc79jkeuit2" />
          </div>}
        {isColumnVisible('id') && <button onClick={() => handleSort('id')} className={`flex items-center text-left transition-colors hover:text-gray-200 ${sortColumn === 'id' ? 'text-amber' : ''}`} data-id="element-236" data-mp="3u9vx3v24keqp">
            Bates #<SortIcon column="id" data-id="element-237" data-mp="3lcvantym8qpw" />
          </button>}

        {/* Gear icon — column settings */}
        <div className="relative flex justify-end" ref={columnDropdownRef} data-id="element-238" data-mp="36k1j3a62kk4v">
          <button onClick={() => setColumnDropdownOpen(!columnDropdownOpen)} className={`p-0.5 rounded transition-colors ${columnDropdownOpen ? 'text-amber' : 'text-gray-500 hover:text-gray-300'}`} title="Toggle columns" data-id="element-239" data-mp="3bl8ox3onj98w">
            <Settings2Icon className="w-3.5 h-3.5" data-id="element-240" data-mp="1854119929tum" />
          </button>
          {columnDropdownOpen && <div className="absolute top-full right-0 mt-1 w-48 bg-navy border border-navy-lighter rounded-lg shadow-xl z-50 py-2" data-id="element-241" data-mp="13ckevnwb5qe7">
              <div className="px-3 py-1.5 text-[10px] text-gray-500 uppercase tracking-wider font-semibold" data-id="element-242" data-mp="yc6comhe1ao3">
                Visible Columns
              </div>
              {TABLE_COLUMNS.map(col => {
            const visible = visibleColumns.has(col.id);
            return <button key={col.id} onClick={() => toggleColumn(col.id)} disabled={col.required} className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left text-xs transition-colors ${col.required ? 'opacity-40 cursor-not-allowed' : 'hover:bg-navy-hover'}`} data-id="element-243" data-mp="1m1qz5fcr2q24">
                    <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors ${visible ? 'bg-amber border-amber' : 'border-navy-lighter bg-navy'}`} data-id="element-244" data-mp="1n76re4a0kz2">
                      {visible && <CheckIcon className="w-2.5 h-2.5 text-navy" data-id="element-245" data-mp="2mbibm9lzcq4l" />}
                    </div>
                    <span className={visible ? 'text-white' : 'text-gray-400'} data-id="element-246" data-mp="2r3s7e69s0kes">
                      {col.label}
                    </span>
                    {col.required && <span className="text-[9px] text-gray-500 ml-auto" data-id="element-247" data-mp="310epwoemqzdn">
                        Required
                      </span>}
                  </button>;
          })}
            </div>}
        </div>
      </div>

      {/* Table Body */}
      <div className="flex-1 overflow-auto" data-id="element-248" data-mp="1ap0els8pxb4y">
        {paginatedRows.length === 0 ? <div className="flex flex-col items-center justify-center py-20 text-center" data-id="element-249" data-mp="1fnlchl0hziq8">
            <SearchIcon className="w-10 h-10 text-gray-600 mb-3" data-id="element-250" data-mp="1fpelv82hwh1x" />
            <p className="text-sm text-gray-400" data-id="element-251" data-mp="3hjkkg9abhet0">
              No documents match your filters
            </p>
            <button onClick={clearAllFilters} className="mt-3 text-sm text-amber hover:text-amber-hover transition-colors" data-id="element-252" data-mp="3dhfsrg3fis90">
              Clear all filters
            </button>
          </div> : paginatedRows.map(({
        doc,
        isChild,
        isTerminal
      }) => {
        const isSelected = selectedDoc?.id === doc.id;
        const isThreadParent = doc.threadId && doc.threadPosition === 1;
        const threadExpanded = doc.threadId ? expandedThreads.has(doc.threadId) : false;
        const threadSize = doc.threadId ? getThreadSize(doc.threadId) : 0;
        const state = docStates[doc.id] || DEFAULT_REVIEW_STATE;
        const isPrivileged = state.privileged;
        const isReviewed = state.reviewed;
        const currentDocIssues = docIssues[doc.id] || [];
        const activeIssues = currentDocIssues.map(id => caseIssues.find(i => i.id === id)).filter(Boolean) as CaseIssue[];
        return <div key={doc.id} className={`
                  border-b border-navy-lighter transition-colors cursor-pointer
                  ${isChild ? 'ml-5 border-l border-l-navy-lighter' : ''}
                  ${isChild && !isPrivileged ? 'bg-[#0C1825]' : ''}
                  ${isTerminal && !isSelected && !isPrivileged ? 'border-l-2 border-l-amber' : ''}
                  ${isPrivileged && !isSelected ? 'bg-red-500/[0.08] border-l-[3px] border-l-red-500 shadow-[inset_0_0_30px_rgba(239,68,68,0.06)]' : ''}
                  ${isSelected && isPrivileged ? 'bg-red-500/[0.12] border-l-[3px] border-l-red-500' : ''}
                  ${isSelected && !isPrivileged ? 'bg-amber/[0.12] border-l-[3px] border-l-amber shadow-[inset_0_0_30px_rgba(245,158,11,0.06)]' : ''}
                  ${!isSelected && !isChild && !isPrivileged ? 'hover:bg-navy-hover' : ''}
                  ${!isSelected && isChild && !isPrivileged ? 'hover:bg-[#132838]' : ''}
                  ${!isSelected && isPrivileged ? 'hover:bg-red-500/[0.14]' : ''}
                `} onClick={() => {
          setHoveredDoc(null);
          setSelectedDoc(doc);
        }} onMouseEnter={e => handleRowMouseEnter(doc, e)} onMouseLeave={handleRowMouseLeave} data-id="element-253" data-mp="1rstz0lu6mck7">
                {/* Main grid row */}
                <div className={`grid gap-2 items-center text-sm ${isChild ? 'pl-12 pr-6 py-2' : 'px-6 py-3'}`} style={{
            gridTemplateColumns: gridTemplate
          }} data-id="element-254" data-mp="155hfxc5ty5xa">
                  {/* Date */}
                  {isColumnVisible('date') && <div className={`text-xs ${isPrivileged ? 'text-red-400' : 'text-gray-300'}`} data-id="element-255" data-mp="1122rbx3tsh88">
                      {doc.date}
                    </div>}

                  {/* Type */}
                  {isColumnVisible('type') && <div data-id="element-256" data-mp="qjtvdpvpszdw">
                      <Badge variant={getTypeBadgeVariant(doc.type)} data-id="element-257" data-mp="qg63ev5x4l1a">
                        {doc.type}
                      </Badge>
                    </div>}

                  {/* Subject */}
                  {isColumnVisible('subject') && <div className="flex items-center min-w-0" data-id="element-258" data-mp="vgkb2c3lxsys">
                      {isChild && <span className={`mr-1 shrink-0 ${isPrivileged ? 'text-red-400' : 'text-gray-500'}`} data-id="element-259" data-mp="1a5q75r75f32g">
                          ↳
                        </span>}
                      {isReviewed && <CheckCircleIcon className={`w-3.5 h-3.5 mr-1.5 shrink-0 ${isPrivileged ? 'text-red-400' : 'text-green-500'}`} title="Reviewed" data-id="element-260" data-mp="1e8w9wov1yc46" />}
                      <div className={`text-sm font-medium truncate ${isPrivileged ? 'text-red-300' : 'text-white'}`} data-id="element-261" data-mp="kv510u3aznny">
                        {doc.subject}
                      </div>
                    </div>}

                  {/* AI Summary (short) */}
                  {isColumnVisible('summary') && <div className={`text-sm truncate pr-2 ${isPrivileged ? 'text-red-400/70' : 'text-gray-400'}`} data-id="element-262" data-mp="zi6m77aj2yhu">
                      {doc.shortSummary || doc.summary}
                    </div>}

                  {/* Sender */}
                  {isColumnVisible('sender') && <div className={`text-xs truncate ${isPrivileged ? 'text-red-400' : 'text-gray-300'}`} data-id="element-263" data-mp="3abj48dke3qbk">
                      {doc.sender}
                    </div>}

                  {/* Status */}
                  {isColumnVisible('status') && <div data-id="element-264" data-mp="2t3rzwjqaceqs">
                      <Badge variant={getStatusBadgeVariant(doc.status)} data-id="element-265" data-mp="2t39xt6gxz6ay">
                        {doc.status}
                      </Badge>
                      {isTerminal && <Badge variant="amber" className="ml-1 text-[10px] px-1 py-0" data-id="element-266" data-mp="3lq4dzr6q7jim">
                          Terminal
                        </Badge>}
                    </div>}

                  {/* Thread indicator */}
                  {isColumnVisible('thread') && <div className="flex justify-center" data-id="element-267" data-mp="21clsrjc5dgs0">
                      {isThreadParent && <button onClick={e => {
                e.stopPropagation();
                toggleThread(doc.threadId!);
              }} className="flex items-center gap-0.5 text-gray-400 hover:text-amber transition-colors p-1 rounded" title={`${threadSize} emails in thread`} data-id="element-268" data-mp="1wbofnl5zvcxd">
                          <ChevronRightIcon className={`w-3.5 h-3.5 transition-transform duration-150 ${threadExpanded ? 'rotate-90' : ''}`} data-id="element-269" data-mp="3f7zv67m9f2m0" />
                          <span className="text-xs font-medium" data-id="element-270" data-mp="bsklx8lpgx1x">
                            {threadSize}
                          </span>
                        </button>}
                    </div>}

                  {/* Open in new tab */}
                  {isColumnVisible('action') && <div className="flex justify-end" data-id="element-271" data-mp="2nin0j35f1j35">
                      {!isChild && <button onClick={e => {
                e.stopPropagation();
                window.open(`/documents/${doc.id}`, '_blank');
              }} className="p-1.5 text-gray-500 hover:text-amber hover:bg-navy rounded transition-colors" title="Open document in new tab" data-id="element-272" data-mp="2f1w6dvvsmuqc">
                          <ExternalLinkIcon className="w-4 h-4" data-id="element-273" data-mp="1wamq93rip7ps" />
                        </button>}
                    </div>}

                  {/* Bates # (far right) */}
                  {isColumnVisible('id') && <div className={`font-mono text-xs truncate ${isPrivileged ? 'text-red-400' : 'text-gray-500'}`} data-id="element-274" data-mp="2630uq49zcsfn">
                      {doc.id}
                    </div>}

                  {/* Spacer for gear column */}
                  <div data-id="element-275" data-mp="18jprksoqg6wh" />
                </div>

                {/* Issues second line */}
                {activeIssues.length > 0 && <div className={`flex gap-1.5 pb-2 ${isChild ? 'pl-12 pr-6' : 'px-6'}`} data-id="element-276" data-mp="3dmmpgt4loa5f">
                    {activeIssues.map(issue => {
              const isActiveFilter = issueFilter === issue.id;
              return <button key={issue.id} onClick={e => {
                e.stopPropagation();
                onIssueClick(issue.id);
              }} className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium transition-all hover:opacity-80 ${isActiveFilter ? 'ring-1 ring-offset-1 ring-offset-navy' : ''}`} style={{
                color: issue.color,
                backgroundColor: issue.color + '15',
                ...(isActiveFilter ? {
                  ringColor: issue.color
                } : {})
              }} title={`Filter by ${issue.label}`} data-id="element-277" data-mp="2pph3zia51fb9">
                          <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{
                  backgroundColor: issue.color
                }} data-id="element-278" data-mp="fa8e54an9vaz" />
                          {issue.label}
                        </button>;
            })}
                  </div>}
              </div>;
      })}
      </div>

      {/* Pagination Footer */}
      <div className="border-t border-navy-lighter bg-navy-light px-6 py-2.5 flex items-center justify-between text-xs text-gray-400 shrink-0" data-id="element-279" data-mp="1uqqqktgzoh6s">
        <span data-id="element-280" data-mp="29z6n3i1ycxte">
          {totalFiltered === 0 ? 'No documents' : `Showing ${showingFrom}–${showingTo} of ${totalFiltered} documents`}
        </span>

        <div className="flex items-center gap-4" data-id="element-281" data-mp="2e009u83ns6kp">
          {/* Page size selector */}
          <div className="relative" ref={pageSizeDropdownRef} data-id="element-282" data-mp="208vhlr2megke">
            <button onClick={() => setPageSizeDropdownOpen(!pageSizeDropdownOpen)} className="flex items-center gap-1 px-2 py-1 bg-navy border border-navy-lighter rounded text-gray-300 hover:bg-navy-hover transition-colors" data-id="element-283" data-mp="257yg0so3hoje">
              {pageSize} / page
              <ChevronDownIcon className="w-3 h-3 opacity-50" data-id="element-284" data-mp="1hb2iz0z14sst" />
            </button>
            {pageSizeDropdownOpen && <div className="absolute bottom-full right-0 mb-1 w-24 bg-navy border border-navy-lighter rounded-lg shadow-xl z-50 py-1" data-id="element-285" data-mp="1maf33dyinlrp">
                {([25, 50, 100] as const).map(size => <button key={size} onClick={() => {
              setPageSize(size);
              setPageSizeDropdownOpen(false);
            }} className={`w-full px-3 py-1.5 text-left text-xs transition-colors ${pageSize === size ? 'text-amber bg-amber/5' : 'text-gray-300 hover:bg-navy-hover'}`} data-id="element-286" data-mp="3gmbytj3zrdsk">
                    {size}
                  </button>)}
              </div>}
          </div>

          {/* Prev / Next */}
          <div className="flex items-center gap-1" data-id="element-287" data-mp="8dkocgvq1svz">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={safeCurrentPage <= 1} className="p-1 text-gray-400 hover:text-white rounded transition-colors disabled:opacity-25 disabled:cursor-not-allowed" data-id="element-288" data-mp="8d2lh1ek0gf3">
              <ChevronUpIcon className="w-4 h-4 -rotate-90" data-id="element-289" data-mp="2o5i0r5y5ma68" />
            </button>
            <span className="text-gray-300 min-w-[60px] text-center" data-id="element-290" data-mp="2k3wui3g4ku50">
              Page {safeCurrentPage} of {totalPages}
            </span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safeCurrentPage >= totalPages} className="p-1 text-gray-400 hover:text-white rounded transition-colors disabled:opacity-25 disabled:cursor-not-allowed" data-id="element-291" data-mp="1rfburyzg1mv4">
              <ChevronDownIcon className="w-4 h-4 -rotate-90" data-id="element-292" data-mp="1w9znyb5e75g1" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}